import { useTranslation } from 'react-i18next'
import { MemberRole, TMember } from '../../../../types/commonTypes'
import styles from './MembersTable.module.css'
import { useCallback, useMemo, useRef, useState } from 'react'
import { CellElement, Table, TableRow } from '../../../reports/components/GenericTable'
import { ToggleSwitch } from '../../../../common/components/ToggleSwitch/ToggleSwitch'
import { getMemberRoleOptions } from '../helpers'
import { Select } from '../../../../common/components/Select/Select'
import { Button } from '../../../../common/components/button/Button'
import { getIcon } from '../../../../common/components/icons/utils'
import { useSortedTable } from '../../../../hooks/useSortedTable'
import { ReportSortDir } from '../../../../contexts/ReportsContextProvider'

type MembersTableProps = {
  members: TMember[]
  isLoading: boolean
  onChangeMemberRole: (userName: string) => Promise<boolean>
  onToggleMemberSaigeAccess: (userName: string) => Promise<boolean>
  onRemoveMember: (userName: string) => Promise<boolean>
  orgHasSaigeAccess: boolean
  saigeAllMembers?: boolean
}

export const MembersTable: React.FC<MembersTableProps> = ({
  members,
  isLoading,
  onRemoveMember,
  onChangeMemberRole,
  onToggleMemberSaigeAccess,
  orgHasSaigeAccess,
  saigeAllMembers,
}) => {
  const { t } = useTranslation()

  const [isEditingRoleOf, setIsEditingRoleOf] = useState<number>()
  const [isRemovingMember, setIsRemovingMember] = useState<boolean>(false)
  const [isSubmittingRoleChange, setIsSubmittingRoleChange] = useState<boolean>(false)
  const [newRole, setNewRole] = useState<MemberRole>(MemberRole.INSTRUCTOR)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const scrollRef = useRef<HTMLDivElement>(null)

  const {
    sortedItems: sortedMembers,
    sortState,
    setSort,
  } = useSortedTable<TMember>({
    items: members,
    initialSort: { sortBy: 'name', direction: ReportSortDir.ASC },
    getSortableValue: (member, key) => {
      const value = member[key]

      if (typeof value === 'string') return value.trim().toLocaleLowerCase()
      if (typeof value === 'number' || typeof value === 'boolean') return value

      // Fallback for undefined/null or unexpected types
      return ''
    },
  })

  const headers = orgHasSaigeAccess
    ? [
        t('settings_organization.members.table_headers.name', 'Name'),
        t('settings_organization.members.table_headers.email', 'Email'),
        t('settings_organization.members.table_headers.id', 'ID'),
        t('settings_organization.members.table_headers.saige', 'AI'),
        t('settings_organization.members.table_headers.role', 'Role'),
        t('settings_organization.members.table_headers.actions', 'Actions'),
      ]
    : [
        t('settings_organization.members.table_headers.name', 'Name'),
        t('settings_organization.members.table_headers.email', 'Email'),
        t('settings_organization.members.table_headers.id', 'ID'),
        t('settings_organization.members.table_headers.role', 'Role'),
        t('settings_organization.members.table_headers.actions', 'Actions'),
      ]

  const handleSaigeAccessToggle = useCallback(
    (email: string) => () => {
      onToggleMemberSaigeAccess(email)
    },
    [onToggleMemberSaigeAccess],
  )

  const createSaigeToggleCell = useCallback(
    (member: TMember) => {
      return (
        <div className={styles.saigeCell}>
          <ToggleSwitch
            onChange={handleSaigeAccessToggle(member.email)}
            checked={saigeAllMembers || member.saigeAccess}
          />
        </div>
      )
    },
    [handleSaigeAccessToggle, saigeAllMembers],
  )

  const onChangeMemberRoleInternal = useCallback(
    async (member: TMember) => {
      if (member.role === newRole) {
        setIsEditingRoleOf(undefined)
        return
      }
      setIsSubmittingRoleChange(true)
      await onChangeMemberRole(member.email)
      setIsSubmittingRoleChange(false)
      setIsEditingRoleOf(undefined)
    },
    [newRole, onChangeMemberRole],
  )

  const onRemoveMemberInternal = useCallback(
    async (member: TMember) => {
      setIsRemovingMember(true)
      await onRemoveMember(member.email)
      setIsRemovingMember(false)
    },
    [onRemoveMember],
  )

  const memberRoleOptions = getMemberRoleOptions(t)

  const createRoleCell = useCallback(
    (member: TMember) => {
      if (isEditingRoleOf === member.id)
        return (
          <Select
            className={styles.roleEditSelect}
            options={memberRoleOptions}
            value={memberRoleOptions.find((option) => option.value === newRole)}
            onChange={(option) => setNewRole((option?.value as MemberRole) || member.role)}
          />
        )
      if (member.role === MemberRole.ADMIN) return t('settings_organization.members.role_label.admin', 'Admin')
      return t('settings_organization.members.role_label.instructor', 'Instructor')
    },
    [isEditingRoleOf, memberRoleOptions, newRole, t],
  )

  const createActionCell = useCallback(
    (member: TMember) => {
      if (isEditingRoleOf === member.id)
        return (
          <div className={styles.memberEditingRoleActionsContainer}>
            <Button
              variant='outline-tiny'
              key={`cancel-button-${member.id}`}
              onClick={() => setIsEditingRoleOf(undefined)}
              disabled={isSubmittingRoleChange}
            >
              {t('settings_organization.members.buttons_text.cancel', 'Cancel')}
            </Button>
            <Button
              variant='tiny'
              key={`ok-button-${member.id}`}
              disabled={isSubmittingRoleChange}
              onClick={() => onChangeMemberRoleInternal(member)}
            >
              {t('settings_organization.members.buttons_text.submit_role_update', 'Ok')}
            </Button>
          </div>
        )
      return (
        <div className={styles.memberActionsContainer}>
          <button
            key={`change-role-button-${member.id}`}
            onClick={() => {
              setIsEditingRoleOf(member.id)
              setNewRole(member.role)
            }}
          >
            <span>{getIcon('text')}</span>
            <span>{t('settings_organization.members.action_text.change_role', 'Change role')}</span>
          </button>
          <button
            key={`remove-button-${member.id}`}
            onClick={() => onRemoveMemberInternal(member)}
            disabled={isRemovingMember}
          >
            <span>{getIcon('trash')}</span>
            <span>{t('settings_organization.members.action_text.remove_member', 'Remove member')}</span>
          </button>
        </div>
      )
    },
    [isEditingRoleOf, isRemovingMember, isSubmittingRoleChange, onChangeMemberRoleInternal, onRemoveMemberInternal, t],
  )

  const tableRowData = useMemo((): TableRow[] => {
    const rows: TableRow[] = []

    for (let i = (currentPage - 1) * pageSize; i < currentPage * pageSize && i < sortedMembers.length; i++) {
      const row = sortedMembers[i] as TMember
      const tableRow: CellElement[] = orgHasSaigeAccess
        ? [row.name, row.email, row.orgId, createSaigeToggleCell(row), createRoleCell(row), createActionCell(row)]
        : [row.name, row.email, row.orgId, createRoleCell(row), createActionCell(row)]
      rows.push(tableRow)
    }
    return rows
  }, [createActionCell, createRoleCell, createSaigeToggleCell, currentPage, orgHasSaigeAccess, pageSize, sortedMembers])

  const lastPage = Math.floor(sortedMembers.length / pageSize) + (sortedMembers.length % pageSize === 0 ? 0 : 1)
  const allPageOptions = Array.from({ length: lastPage }, (_, i) => ({
    value: i + 1,
    label: `${i + 1}`,
  }))

  return (
    <div className={styles.tableContainer} ref={scrollRef}>
      <Table
        headerWidths={
          orgHasSaigeAccess
            ? ['30%', '50%', '20%', 'fit-content', 'fit-content', 'fit-content']
            : ['30%', '50%', '20%', 'fit-content', 'fit-content']
        }
        headerLabels={headers}
        rows={tableRowData}
        loading={isLoading}
        hasPagination
        currentPage={currentPage}
        pageSize={pageSize}
        lastPage={lastPage}
        allPageOptions={allPageOptions}
        //loading
        onChangeCurrentPage={(option) => {
          scrollRef.current?.scrollIntoView()
          setCurrentPage(option?.value || 1)
        }}
        onChangePageSize={(option) => {
          scrollRef.current?.scrollIntoView()
          setPageSize(option?.value || 10)
          setCurrentPage(1)
        }}
        onClickNext={() => {
          scrollRef.current?.scrollIntoView()
          setCurrentPage((currentPage || 0) + 1)
        }}
        onClickPrev={() => {
          scrollRef.current?.scrollIntoView()
          setCurrentPage((currentPage || 2) - 1)
        }}
        itemsPerPageLabel={t('settings_organization.members.per_page', 'members per page')}
        isSortable
        headerSortTags={['name', 'email', 'orgId', ...(orgHasSaigeAccess ? ['saigeAccess'] : []), 'role']}
        onSort={(sortKey: string) => (direction: string) => {
          setSort(sortKey as keyof TMember, direction as ReportSortDir)
        }}
        activeSortCol={sortState.sortBy}
        noSortIndexes={
          orgHasSaigeAccess ? [false, false, false, false, false, true] : [false, false, false, false, true]
        }
        customClassName={styles.membersTable}
      />
    </div>
  )
}
